import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaLocationArrow, FaEnvelope, FaCommentDots, FaEye, FaTimes } from "react-icons/fa";
import { AUTH_STORAGE_KEY } from "../socket";
import CommonNavbar from "../components/CommonNavbar";
import FooterPage from "../components/Footer";
import FloatingSideButtons from "../components/FloatingSideButtons";

const CATEGORY_ICONS = {
  plumber: "💧",
  plumbing: "💧",
  electrician: "⚡",
  electrical: "⚡",
  carpenter: "🪵",
  carpentry: "🪵",
  painter: "🎨",
  painting: "🎨",
  pest: "🐜",
  "pest control": "🐜",
  cleaning: "🧹",
  housekeeping: "🧹",
  ac: "❄️",
  "ac repair": "❄️",
  hvac: "❄️",
  appliance: "📦",
  refrigerator: "🧊",
  salon: "💇",
  salons: "💇",
  beauty: "💄",
  spa: "🧖",
  massage: "🧖",
  wellness: "🧖",
  gym: "🏋️",
  gyms: "🏋️",
  fitness: "🏋️",
  yoga: "🧘",
  "yoga classes": "🧘",
  hospital: "🏥",
  dentist: "🦷",
  cardiologist: "❤️",
  neurologist: "🧠",
  dermatologist: "🩺",
  "gynaecologist": "👩‍⚕️",
  "real estate": "🏠",
  restaurant: "🍽️",
  cafe: "☕",
  bakery: "🥐",
  car: "🚗",
  cars: "🚗",
  "car rental": "🚘",
  website: "🌐",
  software: "💻",
  developer: "👨‍💻",
  accountant: "🧮",
  lawyer: "⚖️",
  marketing: "📈",
  agriculture: "🌾",
};

function getIconForCategory(name) {
  const key = String(name || "").trim().toLowerCase();
  for (const [pattern, icon] of Object.entries(CATEGORY_ICONS)) {
    if (key === pattern || key.includes(pattern)) return icon;
  }
  return "🔧";
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function normalizeCategory(cat) {
  const raw = String(cat || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("plumb")) return "plumbing";
  if (raw.includes("electric")) return "electrical";
  if (raw.includes("carpen")) return "carpentry";
  return raw;
}

function authHeader() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.token ? { Authorization: `Bearer ${parsed.token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
  } catch {
    return { "Content-Type": "application/json" };
  }
}

async function createLead(serviceId) {
  if (!serviceId) return;
  try {
    const now = new Date();
    const scheduledDate = now.toISOString().slice(0, 10);
    const scheduledTime = now.toTimeString().slice(0, 5);
    await fetch("/api/bookings", {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify({
        serviceId,
        scheduledDate,
        scheduledTime,
        notes: "Lead created from service listing",
      }),
    });
  } catch (err) {
    console.warn("Failed to create lead", err);
  }
}

async function savePredefinedChatMessage(toEmail, serviceName) {
  if (!toEmail) return;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const currentUser = parsed?.user?.email?.toLowerCase() || "";
    if (!currentUser) return;
    const predefinedMessage = `Hi! I'm interested in your ${serviceName || "service"}. Can we discuss details?`;
    await fetch("/save-message", {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify({
        from: currentUser,
        to: toEmail,
        text: predefinedMessage,
        type: "text",
        statusTag: "open",
        time: Date.now(),
      }),
    });
  } catch (err) {
    console.warn("Failed to save predefined message", err);
  }
}

function distanceBadgeStyle(km) {
  const isNear = km <= 5;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 7px",
    borderRadius: 10,
    background: isNear ? "#e6f9f0" : "#f5f5f5",
    color: isNear ? "#1a7a4a" : "#666",
    border: `1px solid ${isNear ? "#a3e0c0" : "#ddd"}`,
    marginTop: 2,
  };
}

function ServiceCard({ s, userLocation }) {
  const navigate = useNavigate();
  const [imageIndex, setImageIndex] = useState(0);

  const galleryImages = useMemo(() => {
    const businessImage = s.img || "";
    const catalogImages = (Array.isArray(s.catalogItems) ? s.catalogItems : [])
      .filter((item) => {
        const status = String(item?.approvalStatus || item?.status || "").toLowerCase();
        if (status === "pending" || status === "rejected") return false;
        if (item?.isApproved === false) return false;
        return true;
      })
      .map((item) => item.photoUrl || item.image || "")
      .filter(Boolean);
    return [businessImage, ...catalogImages].filter(Boolean);
  }, [s]);

  useEffect(() => {
    if (galleryImages.length <= 1) return;
    const timer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % galleryImages.length);
    }, 2800);
    return () => clearInterval(timer);
  }, [galleryImages.length]);

  const distanceKm = userLocation && s.lat && s.lng
    ? haversineKm(userLocation.lat, userLocation.lng, parseFloat(s.lat), parseFloat(s.lng))
    : null;

  const recordInteraction = async () => {
    if (!localStorage.getItem(AUTH_STORAGE_KEY)) return;
    await Promise.allSettled([
      createLead(s._id),
      savePredefinedChatMessage(s.ownerId, s.businessName || s.name),
    ]);
  };

  const handleMail = async (e) => {
    e?.stopPropagation();
    if (!localStorage.getItem(AUTH_STORAGE_KEY)) { navigate("/login"); return; }
    await recordInteraction();
    navigate("/kmail", { state: { composeTo: s.ownerId } });
  };

  const handleChat = async (e) => {
    e?.stopPropagation();
    if (!localStorage.getItem(AUTH_STORAGE_KEY)) { navigate("/login"); return; }
    await recordInteraction();
    navigate("/kmail/Krivachat", { state: { email: s.ownerId } });
  };

  const handleViewBusiness = () => {
    if (localStorage.getItem(AUTH_STORAGE_KEY)) {
      createLead(s._id).catch(() => {});
      savePredefinedChatMessage(s.ownerId, s.name).catch(() => {});
    }
    const businessSlug = String(s.businessName || s.name || "business")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[ -]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "business";
    navigate(`/${businessSlug}-${s._id}`);
  };

  return (
    <div className="eb-service-card" onClick={handleViewBusiness} style={{ cursor: "pointer" }}>
      <div className="eb-service-slider" onClick={(e) => e.stopPropagation()}>
        <div className="eb-service-slider-track" style={{ transform: `translateX(-${imageIndex * 100}%)` }}>
          {galleryImages.map((img, index) => (
            <div key={`${s._id}-${index}`} className="eb-service-slide">
              <img src={img} alt={`${s.name} image ${index + 1}`} onError={(e) => { e.target.style.display = "none"; }} />
            </div>
          ))}
        </div>
      </div>
      <div className="eb-service-body">
        {s.badge && <div className="eb-service-badge">{s.badge}</div>}
        {s.businessName ? <div className="eb-service-cat">{s.category}</div> : null}
        <div className="eb-service-name">{s.businessName ? s.name : s.category || "Service"}</div>
        <div className="eb-service-desc">{s.description}</div>
        <div className="eb-service-footer">
          <div className="eb-service-meta">
            <div className="eb-service-area">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M6 1a4 4 0 0 1 4 4c0 3-4 6-4 6S2 8 2 5a4 4 0 0 1 4-4z" stroke="#888" strokeWidth="1.3" />
                <circle cx="6" cy="5" r="1.3" stroke="#888" strokeWidth="1.1" />
              </svg>
              {s.area?.length > 50 ? `${s.area.slice(0, 50)}...` : s.area}
            </div>
            {distanceKm !== null && (
              <div style={distanceBadgeStyle(distanceKm)}>
                📍 {distanceKm < 1 ? `${Math.round(distanceKm * 1000)} m` : `${distanceKm.toFixed(1)} km`} away
              </div>
            )}
            <div className="eb-service-area">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3.1 1.8C3.1 1.4 3.4 1.1 3.8 1.1H4.8C5.1 1.1 5.4 1.3 5.5 1.6L5.9 3C6 3.3 5.9 3.6 5.7 3.8L5.1 4.4C5.6 5.5 6.5 6.4 7.6 6.9L8.2 6.3C8.4 6.1 8.7 6 9 6.1L10.4 6.5C10.7 6.6 10.9 6.9 10.9 7.2V8.2C10.9 8.6 10.6 8.9 10.2 8.9C6.3 8.9 3.1 5.7 3.1 1.8Z" stroke="#888" strokeWidth="1.1" strokeLinejoin="round" />
              </svg>
              {s.ownerId}
            </div>
          </div>
          <div className="eb-service-actions" style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
            <button onClick={(e) => { e.stopPropagation(); handleViewBusiness(); }} style={btnStyle("#6b7280", "#4b5563", "#fff")}> 
              <FaEye size={14} /> View
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleMail(); }} style={btnStyle("#6dd5ed", "#2193b0", "#fff")}> 
              <FaEnvelope size={14} /> Mail
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleChat(); }} style={btnStyle("#f7971e", "#ffd200", "#222")}> 
              <FaCommentDots size={14} /> Chat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function btnStyle(c1, c2, textColor) {
  return {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: `linear-gradient(90deg, ${c1} 0%, ${c2} 100%)`,
    color: textColor,
    border: "none",
    borderRadius: 13,
    padding: "8px 12px",
    fontWeight: 600,
    fontSize: 13,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };
}

function SearchResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [nearbyRadius, setNearbyRadius] = useState(10);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const query = params.get("q") || "";
  const category = params.get("category") || "";
  const selectedCity = params.get("city") || "";

  useEffect(() => {
    setSearchQuery(query || category || "");
  }, [query, category]);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const res = await fetch("/api/allservices");
        const data = await res.json();
        const mapped = (data.services || []).map((s) => ({
          _id: s._id,
          category: s.category,
          businessName: s.businessName || "",
          catalogItems: Array.isArray(s.catalogItems) ? s.catalogItems.filter((item) => {
            const status = String(item?.approvalStatus || item?.status || "").toLowerCase();
            if (status === "pending" || status === "rejected") return false;
            if (item?.isApproved === false) return false;
            return true;
          }) : [],
          name: s.businessName || s.category || "Business",
          description: s.description,
          area: s.area,
          city: s.city || "",
          phone: s.phone,
          ownerId: s.ownerId,
          lat: s.lat ? parseFloat(s.lat) : null,
          lng: s.lng ? parseFloat(s.lng) : null,
          rating: 4.5,
          badge: "Verified",
          img: s.photoUrl || "https://via.placeholder.com/400",
        }));

        const normalizedQuery = String(category || query || "").trim().toLowerCase();
        const normalizedCity = String(selectedCity || "").trim().toLowerCase();
        const filtered = mapped.filter((service) => {
          const matchesQuery = !normalizedQuery || [service.category, service.businessName, service.name, service.description, service.area, service.city, service.phone]
            .map((f) => String(f || "").toLowerCase())
            .some((field) => field.includes(normalizedQuery));
          const matchesCity = !normalizedCity || [service.city, service.area]
            .map((f) => String(f || "").toLowerCase())
            .some((field) => field.includes(normalizedCity));
          return matchesQuery && matchesCity;
        });

        setServices(filtered);
      } catch (err) {
        console.error(err);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [query, category]);

  const handleRequestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser.");
      return;
    }
    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationLoading(false);
      },
      () => {
        setLocationLoading(false);
        setLocationError("Could not detect your location. Try searching by area name instead.");
      },
      { timeout: 15000, maximumAge: 300000, enableHighAccuracy: false }
    );
  }, []);

  const handleClearLocation = useCallback(() => {
    setUserLocation(null);
    setLocationError(null);
  }, []);

  return (
    <>
      <FloatingSideButtons />
      <CommonNavbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={setSearchQuery}
        userLocation={userLocation}
        locationLoading={locationLoading}
        locationError={locationError}
        onRequestLocation={handleRequestLocation}
        onClearLocation={handleClearLocation}
        nearbyRadius={nearbyRadius}
      />
      <section className="eb-section eb-services-section" style={{ paddingTop: 120 }}>
        <div className="eb-services-toolbar" style={{ marginBottom: 18 }}>
          <div>
            <h2 className="eb-section-title blackcolor" style={{ marginBottom: 0, color: "black !important" }}>
              Business Listings
            </h2>
            <p style={{ margin: 0, color: "#555" }}>
              Showing results for <strong>{category || query || "all services"}</strong>
              {selectedCity ? <span style={{ marginLeft: 8, color: "#0f766e" }}>in {selectedCity}</span> : null}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button style={nearMeBtnStyle(Boolean(userLocation), locationLoading)} onClick={userLocation ? handleClearLocation : handleRequestLocation}>
              <FaLocationArrow size={13} /> {userLocation ? "Clear" : "Near Me"}
            </button>
            <button
              onClick={() => navigate("/")}
              style={{ borderRadius: 20, padding: "6px 14px", background: "#fff", color: "#333", border: "1px solid #ddd", cursor: "pointer" }}
            >
              Back to home
            </button>
          </div>
        </div>

        {locationError && (
          <div style={{ ...locationStripStyle, background: "#fff3f3", borderColor: "#f5c6c6", color: "#c0392b" }}>
            ⚠️ {locationError}
          </div>
        )}

        <div className="eb-services-grid">
          {loading ? (
            <div>Loading businesses...</div>
          ) : services.length === 0 ? (
            <div>No businesses found.</div>
          ) : (
            services.map((service) => (
              <ServiceCard key={service._id} s={service} userLocation={userLocation} />
            ))
          )}
        </div>
      </section>
      <FooterPage />
    </>
  );
}

const nearMeBtnStyle = (active, loading) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: active ? "linear-gradient(90deg,#1a7a4a,#27ae60)" : "linear-gradient(90deg,#E8820C,#f5a623)",
  color: "#fff",
  border: "none",
  borderRadius: 20,
  padding: "6px 14px",
  fontWeight: 600,
  fontSize: 13,
  cursor: loading ? "wait" : "pointer",
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  opacity: loading ? 0.7 : 1,
  transition: "all 0.2s ease",
});

const locationStripStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "#1a7a4a",
  background: "#e6f9f0",
  border: "1px solid #a3e0c0",
  borderRadius: 8,
  padding: "8px 14px",
  marginBottom: 16,
};

export default SearchResultsPage;
